import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Phone, Mic, Search, AlertCircle, Plus, ArrowRight } from 'lucide-react'
import { api, messaggioErrore } from '../../lib/api'
import { Card } from '../../components/ui/Card'
import { Badge } from '../../components/ui/Badge'
import { Button } from '../../components/ui/Button'
import { EmptyState } from '../../components/ui/EmptyState'
import { Modal } from '../../components/ui/Modal'
import { SessioneVoce } from '../../components/voice/SessioneVoce'
import { etichettaDifficolta, varianteDifficolta } from '../../lib/format'
import pagina from '../pagine.module.css'

interface Lead {
  id: string
  interno: number | null
  nome: string
  ruolo: string
  settore: string
  personalita: string
  obiezioni: string[]
  difficolta: number
  isPublic: boolean
  descrizioneEstesa: string | null
  obiettivoChiamata: string | null
  obiettiviPossibili: string[]
}

interface PendingResp {
  numero_da_chiamare: string
  scade_tra_secondi: number
  sessioni_rimanenti: number | null
}

export function SdrLeads() {
  const queryClient = useQueryClient()
  const [cerca, setCerca] = useState('')
  const [filtroDiff, setFiltroDiff] = useState<number | null>(null)
  const [leadAperto, setLeadAperto] = useState<Lead | null>(null)
  const [risultato, setRisultato] = useState<PendingResp | null>(null)
  const [erroreModale, setErroreModale] = useState<string | null>(null)
  /** Lead per cui è attiva la sessione voce browser (overlay fullscreen) */
  const [sessioneVoceLead, setSessioneVoceLead] = useState<Lead | null>(null)
  /** Obiettivo della chiamata: parte da default lead, l'SDR può cambiarlo */
  const [obiettivoSelez, setObiettivoSelez] = useState<string>('')
  const [obiettivoCustom, setObiettivoCustom] = useState<string>('')
  /** Obiettivo finale: custom prevale sul dropdown */
  const obiettivoFinale = obiettivoCustom.trim() || obiettivoSelez
  /** Obiettivo da passare alla sessione voce browser */
  const [obiettivoSessioneVoce, setObiettivoSessioneVoce] = useState<string>('')

  const q = useQuery({
    queryKey: ['leads'],
    queryFn: async () => (await api.get<{ dati: Lead[] }>('/leads')).data.dati,
  })

  const preseleziona = useMutation({
    mutationFn: async (vars: { leadId: string; obiettivo?: string }) =>
      (await api.post<{ dati: PendingResp }>('/leads/pending', {
        leadId: vars.leadId,
        ...(vars.obiettivo ? { obiettivo_scelto: vars.obiettivo } : {}),
      })).data.dati,
    onSuccess: (data) => {
      setRisultato(data)
      queryClient.invalidateQueries({ queryKey: ['sdr', 'dashboard'] })
    },
    onError: (err) => setErroreModale(messaggioErrore(err)),
  })

  if (q.isLoading) return <div className={pagina.skeleton}>Caricamento…</div>
  if (q.error) return <EmptyState titolo="Errore caricamento lead" />

  const lista = (q.data ?? []).filter((l) => {
    if (filtroDiff && l.difficolta !== filtroDiff) return false
    if (cerca) {
      const t = cerca.toLowerCase()
      return (
        l.nome.toLowerCase().includes(t) ||
        l.ruolo.toLowerCase().includes(t) ||
        l.settore.toLowerCase().includes(t)
      )
    }
    return true
  })

  return (
    <div className={pagina.wrap}>
      <div className={pagina.header}>
        <div>
          <h1 className={pagina.titolo}>Lead</h1>
          <div className={pagina.sottotitolo}>
            Scegli con chi vuoi allenarti — clicca un lead per pre-selezionarlo prima della chiamata.
          </div>
        </div>
      </div>

      <div className={pagina.filtri}>
        <div className={pagina.search}>
          <Search size={16} color="var(--text-muted)" />
          <input
            type="text"
            placeholder="Cerca per nome, ruolo, settore…"
            value={cerca}
            onChange={(e) => setCerca(e.target.value)}
          />
        </div>
        <select
          className={pagina.select}
          value={filtroDiff ?? ''}
          onChange={(e) => setFiltroDiff(e.target.value ? parseInt(e.target.value, 10) : null)}
        >
          <option value="">Tutte le difficoltà</option>
          <option value="1">Facile</option>
          <option value="2">Medio</option>
          <option value="3">Difficile</option>
        </select>
      </div>

      {lista.length === 0 ? (
        <EmptyState
          icona={<Phone size={40} />}
          titolo="Nessun lead trovato"
          descrizione="Modifica i filtri o crea un lead personalizzato in 'I miei Lead'."
        />
      ) : (() => {
        const pubblici = lista.filter((l) => l.isPublic)
        const privati = lista.filter((l) => !l.isPublic)
        return (
          <>
            {/* Lead pubblici (di sistema) */}
            {pubblici.length > 0 && (
              <section>
                <h3 style={{
                  fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)',
                  textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12,
                }}>
                  Lead pubblici
                </h3>
                <div className={pagina.griglia}>
                  {pubblici.map((l) => <CardLead key={l.id} lead={l} onApri={(lead) => {
                    setLeadAperto(lead)
                    setObiettivoSelez(lead.obiettivoChiamata ?? lead.obiettiviPossibili[0] ?? '')
                    setObiettivoCustom('')
                  }} />)}
                </div>
              </section>
            )}

            {/* I tuoi lead (privati + team) */}
            <section style={{ marginTop: pubblici.length > 0 ? 28 : 0 }}>
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12,
              }}>
                <h3 style={{
                  fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)',
                  textTransform: 'uppercase', letterSpacing: '0.06em', margin: 0,
                }}>
                  I tuoi lead
                </h3>
                <Link to="/app/miei-lead" style={{
                  fontSize: 13, color: 'var(--accent)', display: 'inline-flex',
                  alignItems: 'center', gap: 4, fontWeight: 500,
                }}>
                  Gestisci i miei lead <ArrowRight size={13} />
                </Link>
              </div>
              {privati.length === 0 ? (
                <div style={{
                  padding: 24, textAlign: 'center', background: 'var(--bg-subtle)',
                  borderRadius: 'var(--radius-md)', border: '1px dashed var(--border-strong)',
                  fontSize: 14, color: 'var(--text-secondary)',
                }}>
                  Non hai ancora lead personalizzati.{' '}
                  <Link to="/app/miei-lead" style={{ color: 'var(--accent)', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <Plus size={13} /> Crea il tuo primo lead
                  </Link>
                </div>
              ) : (
                <div className={pagina.griglia}>
                  {privati.map((l) => <CardLead key={l.id} lead={l} onApri={(lead) => {
                    setLeadAperto(lead)
                    setObiettivoSelez(lead.obiettivoChiamata ?? lead.obiettiviPossibili[0] ?? '')
                    setObiettivoCustom('')
                  }} />)}
                </div>
              )}
            </section>
          </>
        )
      })()}

      {/* Dettaglio + pre-selezione */}
      <Modal
        aperto={!!leadAperto}
        titolo={leadAperto?.nome ?? ''}
        onChiudi={() => { setLeadAperto(null); setRisultato(null); setErroreModale(null) }}
        larghezzaMax={560}
      >
        {leadAperto && !risultato && (
          <div>
            <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
              <Badge variante={varianteDifficolta(leadAperto.difficolta)}>
                {etichettaDifficolta(leadAperto.difficolta)}
              </Badge>
              <span className={pagina.tag}>{leadAperto.ruolo}</span>
              <span className={pagina.tag}>{leadAperto.settore}</span>
            </div>
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                Personalità
              </div>
              <div style={{ fontSize: 14, lineHeight: 1.6 }}>{leadAperto.personalita}</div>
            </div>
            {leadAperto.descrizioneEstesa && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                  Contesto
                </div>
                <div style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--text-secondary)' }}>
                  {leadAperto.descrizioneEstesa}
                </div>
              </div>
            )}
            {leadAperto.obiezioni.length > 0 && (
              <div style={{ marginBottom: 18 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                  Obiezioni tipiche
                </div>
                <ul style={{ listStyle: 'disc', paddingLeft: 18, fontSize: 14, lineHeight: 1.6 }}>
                  {leadAperto.obiezioni.map((o, i) => (
                    <li key={i}>{o}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Sezione obiettivo della chiamata */}
            <div style={{
              padding: 14, background: 'var(--bg-subtle)',
              borderRadius: 'var(--radius-md)', marginBottom: 14,
            }}>
              <div style={{
                fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)',
                textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8,
              }}>
                🎯 Obiettivo della chiamata
              </div>
              {leadAperto.obiettiviPossibili.length > 0 && (
                <select
                  value={obiettivoSelez}
                  onChange={(e) => setObiettivoSelez(e.target.value)}
                  className={pagina.select}
                  style={{ width: '100%', height: 40, marginBottom: 8 }}
                >
                  {leadAperto.obiettiviPossibili.map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              )}
              <input
                type="text"
                value={obiettivoCustom}
                onChange={(e) => setObiettivoCustom(e.target.value)}
                placeholder="…oppure scrivi il tuo obiettivo specifico"
                className={pagina.input}
                style={{ width: '100%', height: 40 }}
                maxLength={200}
              />
              {obiettivoCustom.trim() && (
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
                  Il tuo testo sovrascrive l'obiettivo selezionato.
                </div>
              )}
            </div>

            {erroreModale && (
              <div style={{
                padding: '10px 12px',
                background: 'var(--danger-soft)',
                color: 'var(--danger)',
                borderRadius: 'var(--radius-md)',
                fontSize: 13,
                marginBottom: 12,
              }}>
                <AlertCircle size={14} style={{ display: 'inline', verticalAlign: -2, marginRight: 6 }} />
                {erroreModale}
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <Button
                taglia="lg"
                variante="secondary"
                iconaSinistra={<Phone size={16} />}
                onClick={() => preseleziona.mutate({ leadId: leadAperto.id, obiettivo: obiettivoFinale })}
                caricamento={preseleziona.isPending}
              >
                Chiama al telefono
              </Button>
              <Button
                taglia="lg"
                iconaSinistra={<Mic size={16} />}
                onClick={() => {
                  const l = leadAperto
                  setObiettivoSessioneVoce(obiettivoFinale)
                  setLeadAperto(null)
                  setSessioneVoceLead(l)
                }}
              >
                Parla nel browser
              </Button>
            </div>
            <div style={{
              marginTop: 10, fontSize: 12, color: 'var(--text-muted)', textAlign: 'center',
            }}>
              Browser più veloce (~1s), telefono migliore qualità in mobilità.
            </div>
          </div>
        )}

        {risultato && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 12 }}>
              Lead pre-selezionato ✓
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 6 }}>
              Chiama questo numero entro {Math.round(risultato.scade_tra_secondi / 60)} minuti:
            </div>
            <div style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 24,
              fontWeight: 700,
              color: 'var(--accent)',
              padding: '14px 20px',
              background: 'var(--accent-soft)',
              borderRadius: 'var(--radius-md)',
              display: 'inline-block',
              marginBottom: 16,
            }}>
              {risultato.numero_da_chiamare}
            </div>
            {risultato.sessioni_rimanenti != null && (
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                {risultato.sessioni_rimanenti} sessioni rimanenti questo mese
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Sessione vocale browser */}
      {sessioneVoceLead && (
        <SessioneVoce
          leadProfileId={sessioneVoceLead.id}
          leadNome={sessioneVoceLead.nome}
          leadRuolo={sessioneVoceLead.ruolo}
          leadSettore={sessioneVoceLead.settore}
          leadDifficolta={sessioneVoceLead.difficolta}
          obiettivoScelto={obiettivoSessioneVoce || undefined}
          onChiudi={() => setSessioneVoceLead(null)}
        />
      )}
    </div>
  )
}

/**
 * Card singola per un Lead nella griglia. Estratta per riutilizzo tra
 * le sezioni "Lead pubblici" e "I tuoi lead".
 */
function CardLead({ lead, onApri }: { lead: Lead; onApri: (l: Lead) => void }) {
  return (
    <Card onClick={() => onApri(lead)} style={{ cursor: 'pointer' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
        <div style={{
          width: 44, height: 44, borderRadius: '50%',
          background: 'var(--accent-soft)', color: 'var(--accent)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <Phone size={20} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 15 }}>{lead.nome}</div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            {lead.ruolo}
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        <Badge variante={varianteDifficolta(lead.difficolta)}>
          {etichettaDifficolta(lead.difficolta)}
        </Badge>
        <span className={pagina.tag}>{lead.settore}</span>
        {lead.interno != null && (
          <span className={pagina.tag}>Int. {lead.interno}</span>
        )}
        {lead.isPublic && <Badge variante="default">Pubblico</Badge>}
      </div>
    </Card>
  )
}
